import { useAppSearchParams } from '@/hooks/use-app-search-params';
import LeftSide from './left-side';
import PrivacyPolicy from './privacy-policy';
import CompanyInformationForm from './company-info';
import PageContainer from '@/components/layout/page-container';
import ContactForm from './contact-info';
import ExpertiseForm from './expertise-and-niche';
import MediaFilesPage from './media';
import ExperiencePage from './experience';
import ProjectsView from './projects';
import { useGetOrganisation } from '@/services/organisations/queries';
import { useGetWorkspaceQuery } from '@/services/workspaces/queries';
import { useAuthStore } from '@/store/auth';
import { showToast } from '@/components/ui/show-toast';
import { Navigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetMapCategories, useGetOrganisationMapData } from '@/services/mapdata/queries';

export default function VerifyPage() {
  const params = useAppSearchParams();
  const view = params.get('view') ?? 'privacy';

  const activeWorkspace = useAuthStore((state) => state.activeWorkspace);
  const organisationId = useAuthStore((state) => state.user?.organisation?.id);
  const logout = useAuthStore(state => state.clearAuthData)

  if (!activeWorkspace) {
    showToast('error', 'Something went wrong!!! Your organisation does not have any workspaces');
    logout()
    return <Navigate to="/sign-in" replace />;
  }

  const { data: workspace } = useGetWorkspaceQuery(activeWorkspace, {
    enabled: !!activeWorkspace,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: organisation, isPending: isLoadingOrganisation } = useGetOrganisation(organisationId ?? '', {
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })

  // get map data
  const { data: mapData, isPending: isLoadingMapData } = useGetOrganisationMapData(activeWorkspace, organisationId ?? '', {
    enabled: !!organisationId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })

  // get map categories
  const { data: mapCategories, isPending: isLoadingMapCategories } = useGetMapCategories(activeWorkspace, undefined, {
    enabled: !!activeWorkspace,
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 3 * 60 * 1000, // 3 minutes
  })

  return (
    <div className='flex min-h-screen md:flex-row'>
      {/* Left sidebar */}
      <LeftSide data={workspace} />
      {/* Right content */}
      {isLoadingOrganisation ? (
        <div className='flex items-center justify-center h-full'>
          <Skeleton className='w-full h-full' />
        </div>
      ) : (
        <PageContainer className='min-h-[100dvh] w-full'>
          {view === 'privacy' && <PrivacyPolicy policy={workspace?.privacyPolicy ?? ''} workspaceName={workspace?.name ?? ''} />}
          {view === 'company-info' && (
            <CompanyInformationForm data={organisation} />
          )}
          {view === 'contact-info' && (
            <ContactForm data={organisation} />
          )}
          {view === 'expertise' && !isLoadingMapCategories && !isLoadingMapData && mapCategories && mapData && organisationId && <ExpertiseForm mapCategories={mapCategories.data} mapData={mapData} organisationId={organisationId} />}
          {view === 'media' && <MediaFilesPage data={organisation} />}
          {view === 'projects' && <ProjectsView data={organisation} />}
          {view === 'experience' && <ExperiencePage organisationId={organisationId ?? ''} />}
        </PageContainer>
      )}
    </div>
  );
}
